import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { getAuthCallbackRedirectUrl, hasAuthCallbackInUrl } from '../lib/authBootstrap';
import { clearAuthCallbackParams } from '../lib/authErrors';
import { ensureCrmUser, ensureDemUser, ensureFormsUser } from '../lib/ensureCrmUser';
import { formatAuthErrorMessage } from '../lib/formatAuthError';
import {
  clearPortalChoice,
  IS_UNIFIED_PORTAL,
  isNeutralAuthPath,
  isSoftPortalEntryPending,
  isSoftPortalSwitchActive,
  markSoftPortalSwitch,
  portalKindFromPath,
  readPortalChoice,
  softPortalEntryTarget,
  writePortalChoice,
  type PortalKind,
} from '../lib/portal';
import { isCalendarAllowlisted } from '../lib/calendarWorkspace';
import {
  pickDisplayProfile,
  portalSideAccess,
  resolveDefaultPortalKind,
  resolvePortalAccess,
  shouldShowPortalChooser,
  type PortalAccessMap,
} from '../lib/portalAccess';
import { isSupabaseConfigured, ensureSecondaryAuthSynced, propagateAuthSession, resetSecondaryAuthSync, setActivePortalKind, supabase, type DbPortalKind } from '../lib/supabase';
import { logPortalOffline } from '../lib/portalPresence';
import { normalizeEmail } from '../lib/emailUtils';
import { normalizeUserRole } from '../lib/settingsPermissions';
import { resolveAuthEmail } from '../lib/authEmail';
import { usePortalAllowlistRealtime } from '../hooks/usePortalAllowlistRealtime';
import type { CrmUser, UserRole } from '../types/crm';

export type AccessStatus =
  | 'idle'
  | 'checking'
  | 'allowed'
  | 'choose_portal'
  | 'not_on_allowlist'
  | 'no_email';

interface AuthContextValue {
  session: Session | null;
  authUser: User | null;
  profile: CrmUser | null;
  role: UserRole;
  accessStatus: AccessStatus;
  loading: boolean;
  configured: boolean;
  authError: string | null;
  deniedEmail: string | null;
  portalAccess: PortalAccessMap | null;
  clearAuthError: () => void;
  signInWithMicrosoft: () => Promise<void>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  /** Soft portal switch: set allowed immediately when access is already known (no full-page verify flash). */
  enterPortal: (kind: PortalKind) => boolean;
  /**
   * Choose→portal / PortalSwitcher preflight: soft markers + Verifying while secondary JWT syncs.
   * Caller awaits ensureSecondaryAuthSynced (when needed), then enterPortal + navigate.
   */
  armPortalEntry: (kind: PortalKind) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const OAUTH_TIMEOUT_MS = 25_000;
const ACCESS_VERIFY_TIMEOUT_MS = 20_000;

function formatAuthError(error: unknown): string {
  if (error instanceof Error) {
    if (error.message.includes('PKCE') || error.message.includes('code verifier')) {
      return 'Sign-in expired. Close this tab, return to the CRM login page, and try Microsoft sign-in again.';
    }
    if (error.message.includes('ACCESS_VERIFY_TIMEOUT')) {
      return 'CRM access check timed out. Run migration 020_sync_allowed_email_on_login.sql in Supabase, then click Retry.';
    }
    return formatAuthErrorMessage(error.message);
  }
  if (typeof error === 'object' && error !== null && 'message' in error) {
    const message = String((error as { message?: unknown }).message ?? '');
    if (message) return formatAuthErrorMessage(message);
  }
  return 'Sign-in failed. Please try again.';
}

function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(label)), ms);
    promise
      .then((v) => {
        clearTimeout(timer);
        resolve(v);
      })
      .catch((e) => {
        clearTimeout(timer);
        reject(e);
      });
  });
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<CrmUser | null>(null);
  const [accessStatus, setAccessStatus] = useState<AccessStatus>('idle');
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);
  const [deniedEmail, setDeniedEmail] = useState<string | null>(null);
  const [portalAccess, setPortalAccess] = useState<PortalAccessMap | null>(null);
  const verifyGeneration = useRef(0);
  const [accessRefreshKey, setAccessRefreshKey] = useState(0);
  const accessRefreshModeRef = useRef<'full' | 'silent'>('full');

  const clearAuthError = useCallback(() => setAuthError(null), []);

  const signOut = useCallback(async () => {
    verifyGeneration.current += 1;
    try {
      await logPortalOffline();
    } catch {
      /* presence log must not block sign out */
    }
    clearPortalChoice();
    setSession(null);
    setProfile(null);
    setAccessStatus('idle');
    setAuthError(null);
    setDeniedEmail(null);
    setPortalAccess(null);
    const { error } = await supabase.auth.signOut({ scope: 'global' });
    if (error) throw error;
  }, []);

  const refreshProfile = useCallback(async () => {
    setAuthError(null);
    if (!session?.user) {
      setProfile(null);
      setAccessStatus('idle');
      return;
    }
    if (isSoftPortalSwitchActive(window.location.pathname)) {
      accessRefreshModeRef.current = 'silent';
      setAccessRefreshKey((k) => k + 1);
      return;
    }
    accessRefreshModeRef.current = 'full';
    setAccessStatus('checking');
    setAccessRefreshKey((k) => k + 1);
  }, [session?.user]);

  const refreshAccessSilent = useCallback(() => {
    if (!session?.user) return;
    accessRefreshModeRef.current = 'silent';
    setAccessRefreshKey((k) => k + 1);
  }, [session?.user]);

  /**
   * Soft portal entry: set allowed + sync JWT from the known session, then caller navigates.
   * No full-page checking, no getSession (avoids auth-lock hangs).
   * Soft markers are only set after allowlist confirms access — never grants a new portal.
   */
  const enterPortal = useCallback(
    (kind: PortalKind): boolean => {
      const map = portalAccess;
      const user = session?.user;
      if (!map || !user || !session) return false;

      const side = portalSideAccess(map, kind);
      if (!side.hasAccess) return false;

      // Cancel any in-flight verify — otherwise it finishes on /choose-portal,
      // clears soft-entry, flashes Verifying, and overwrites elevated role with CRM "user".
      verifyGeneration.current += 1;
      markSoftPortalSwitch(kind);

      const dbKind: DbPortalKind =
        kind === 'it' ? 'it' : kind === 'dem' ? 'dem' : kind === 'forms' ? 'forms' : 'crm';
      setActivePortalKind(dbKind);

      setProfile(side.profile ?? pickDisplayProfile(map));
      setAccessStatus('allowed');
      setAuthError(null);
      setDeniedEmail(null);

      // Same secondary fan-out as verifyAccess (new login): kick it/dem/forms JWT
      // immediately. useGoToPortal awaits ensureSecondaryAuthSynced before navigate.
      if (kind === 'dem' || kind === 'forms' || kind === 'it') {
        void propagateAuthSession(session);
      }

      void (async () => {
        try {
          // Never let ensureDemUser/Forms overwrite an elevated IT/Management profile
          // with dem_allowed_emails role=user (common after auto-login restore).
          const sideRole = side.profile?.role;
          const elevatedSide = sideRole === 'it' || sideRole === 'management';
          if (kind === 'dem' && !map.elevatedDepartment && !elevatedSide) {
            const result = await ensureDemUser(user);
            if (result.status === 'allowed') setProfile(result.profile);
          } else if (kind === 'forms' && !map.elevatedDepartment && !elevatedSide) {
            const result = await ensureFormsUser(user);
            if (result.status === 'allowed') setProfile(result.profile);
          }
        } catch {
          /* background only */
        }
      })();

      return true;
    },
    [portalAccess, session],
  );

  /** Soft markers + Verifying until secondary JWT is ready (or chooser paints Verifying). */
  const armPortalEntry = useCallback(
    (kind: PortalKind): boolean => {
      const map = portalAccess;
      if (!map || !session?.user) return false;
      const side = portalSideAccess(map, kind);
      if (!side.hasAccess) return false;

      verifyGeneration.current += 1;
      markSoftPortalSwitch(kind);

      const dbKind: DbPortalKind =
        kind === 'it' ? 'it' : kind === 'dem' ? 'dem' : kind === 'forms' ? 'forms' : 'crm';
      setActivePortalKind(dbKind);
      setAccessStatus('checking');
      setAuthError(null);
      setDeniedEmail(null);

      if (kind === 'dem' || kind === 'forms' || kind === 'it') {
        void propagateAuthSession(session);
      }
      return true;
    },
    [portalAccess, session],
  );

  useEffect(() => {
    if (!isSupabaseConfigured) {
      setLoading(false);
      return;
    }

    let mounted = true;
    let oauthTimeoutId: ReturnType<typeof setTimeout> | undefined;

    const finishBootstrap = () => {
      if (mounted) setLoading(false);
    };

    if (hasAuthCallbackInUrl()) {
      oauthTimeoutId = setTimeout(() => {
        if (!mounted) return;
        setAuthError('Sign-in timed out. Please try again.');
        clearAuthCallbackParams();
        finishBootstrap();
      }, OAUTH_TIMEOUT_MS);
    }

    void (async () => {
      try {
        const { data, error } = await supabase.auth.getSession();
        if (!mounted) return;
        if (error) {
          setAuthError(formatAuthError(error));
          clearAuthCallbackParams();
        }
        setSession(data.session);
        // Kick verify synchronously for the restored user so we never paint
        // login UI with session + idle between getSession and the effect.
        // Do NOT propagateAuthSession here — onAuthStateChange + verify→chooser
        // warm JWTs. Double setSession on restore races mobile Safari soft-nav.
        if (data.session?.user) {
          setAccessStatus('checking');
        } else {
          setAccessStatus('idle');
        }
      } catch (e) {
        if (mounted) {
          setAuthError(formatAuthError(e));
          clearAuthCallbackParams();
        }
      } finally {
        if (oauthTimeoutId) clearTimeout(oauthTimeoutId);
        finishBootstrap();
      }
    })();

    const { data: sub } = supabase.auth.onAuthStateChange((event, nextSession) => {
      if (!mounted) return;
      // Mobile Safari can emit a transient null session while secondary clients
      // receive setSession during soft choose→portal — that paints Verifying/Login.
      if (!nextSession && isSoftPortalEntryPending() && event !== 'SIGNED_OUT') {
        return;
      }
      setSession(nextSession);
      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        clearAuthCallbackParams();
        setAuthError(null);
      }
      if (event === 'SIGNED_IN') {
        // Don't wipe soft choose→portal markers mid-navigation (causes Verifying flash).
        if (!isSoftPortalEntryPending()) {
          clearPortalChoice();
        }
      }
      if (event === 'SIGNED_OUT') {
        if (isSoftPortalEntryPending()) return;
        resetSecondaryAuthSync();
        clearPortalChoice();
        setProfile(null);
        setAccessStatus('idle');
        setAuthError(null);
        setDeniedEmail(null);
        setPortalAccess(null);
      }
    });

    return () => {
      mounted = false;
      if (oauthTimeoutId) clearTimeout(oauthTimeoutId);
      sub.subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    const user = session?.user;
    if (!user?.id) {
      // Soft choose→portal: keep profile/allowed if Safari briefly clears session.
      if (isSoftPortalEntryPending()) return;
      setProfile(null);
      if (!session) setAccessStatus('idle');
      return;
    }

    const generation = ++verifyGeneration.current;
    const silent = accessRefreshModeRef.current === 'silent';
    accessRefreshModeRef.current = 'full';
    const pathnameAtStart = window.location.pathname;
    const softSwitch =
      isSoftPortalEntryPending() || isSoftPortalSwitchActive(pathnameAtStart);

    if (!silent && !softSwitch) {
      setAccessStatus('checking');
    }
    setAuthError(null);

    // Absolute failsafe — never leave the UI on Verifying forever.
    const failsafeTimer = window.setTimeout(() => {
      if (generation !== verifyGeneration.current) return;
      setAccessStatus((prev) => (prev === 'checking' ? 'idle' : prev));
      setAuthError((prev) => prev ?? 'Access check took too long.');
    }, ACCESS_VERIFY_TIMEOUT_MS + 2_000);

  void (async () => {
      try {
        // DEM/Forms allowlists live on secondary clients — sync JWT first or
        // RLS returns empty and chooser hides DEM even when the user is allowed.
        if (session) {
          void propagateAuthSession(session);
          await ensureSecondaryAuthSynced(4_000);
          if (generation !== verifyGeneration.current) return;
        }

        const map = await withTimeout(
          resolvePortalAccess(user),
          ACCESS_VERIFY_TIMEOUT_MS,
          'ACCESS_VERIFY_TIMEOUT',
        );
        if (generation !== verifyGeneration.current) return;

        // Soft choose→portal: refresh allowlist map only — keep allowed + workspace profile.
        // Check pending kind too: verify may finish while URL is still /choose-portal.
        if (
          softSwitch ||
          isSoftPortalEntryPending() ||
          isSoftPortalSwitchActive(window.location.pathname)
        ) {
          // Mobile often resolves dept slug a beat late; a null elevatedDepartment
          // must not wipe IT elevation or rewrite profile to allowlist role=user
          // (that flashes View-as Admin → User toast while permissions stay admin).
          let appliedMap = map;
          setPortalAccess((prev) => {
            if (prev?.elevatedDepartment && !map.elevatedDepartment) {
              appliedMap = prev;
              return prev;
            }
            appliedMap = map;
            return map;
          });
          const pathKind =
            portalKindFromPath(window.location.pathname) ?? softPortalEntryTarget();
          if (pathKind) {
            const side = portalSideAccess(appliedMap, pathKind);
            if (!side.hasAccess) {
              // Allowlist revoked mid-switch — do not keep a soft-allowed shell.
              clearPortalChoice();
              setProfile(pickDisplayProfile(map));
              setAccessStatus('choose_portal');
              setAuthError(null);
              setDeniedEmail(null);
              return;
            }
            if (side.profile) {
              const nextProfile = side.profile;
              setProfile((prev) => {
                if (appliedMap.elevatedDepartment) return nextProfile;
                const incoming = normalizeUserRole(nextProfile.role);
                const prevRole = prev ? normalizeUserRole(prev.role) : null;
                if (
                  prev &&
                  (prevRole === 'it' || prevRole === 'management') &&
                  incoming === 'user'
                ) {
                  return prev;
                }
                return nextProfile;
              });
            }
          }
          setAccessStatus('allowed');
          setAuthError(null);
          setDeniedEmail(null);
          return;
        }

        if (!map.email) {
          setProfile(null);
          setPortalAccess(null);
          setAccessStatus('no_email');
          setDeniedEmail(null);
          return;
        }

        setPortalAccess(map);
        // JWT fan-out is non-blocking — login must not wait on secondary clients.

        const pathname = window.location.pathname;
        const onCalendar =
          pathname === '/calendar' || pathname.startsWith('/calendar/');
        const onAdmin = pathname === '/admin' || pathname.startsWith('/admin/');

        const hasAnyPortal =
          map.crm.hasAccess ||
          map.it.hasAccess ||
          map.dem.hasAccess ||
          map.forms.hasAccess ||
          map.report.hasAccess ||
          map.accounts.hasAccess ||
          map.purchasing.hasAccess ||
          map.design.hasAccess;

        if (!hasAnyPortal) {
          if (onCalendar) {
            try {
              const calendarOk = await isCalendarAllowlisted(map.email);
              if (generation !== verifyGeneration.current) return;
              if (calendarOk) {
                setProfile(null);
                setAccessStatus('allowed');
                setAuthError(null);
                setDeniedEmail(null);
                return;
              }
            } catch {
              /* fall through to deny */
            }
          }
          setProfile(null);
          setAccessStatus('not_on_allowlist');
          setDeniedEmail(map.email);
          return;
        }

        if (onAdmin || onCalendar) {
          setProfile(pickDisplayProfile(map));
          setAccessStatus('allowed');
          setAuthError(null);
          setDeniedEmail(null);
          return;
        }

        const pathKind = portalKindFromPath(pathname);
        const showChooser = shouldShowPortalChooser(map, pathname);
        let choice = readPortalChoice();

        // Login/callback: drop stale soft-switch choice so multi-portal users
        // always land on /choose-portal (never hang waiting on dem JWT sync).
        // Never clear while a soft choose→portal is in flight.
        if (showChooser && isNeutralAuthPath(pathname) && !isSoftPortalEntryPending()) {
          if (choice) clearPortalChoice();
          choice = null;
          setProfile(pickDisplayProfile(map));
          setAccessStatus('choose_portal');
          return;
        }

        if (showChooser && choice === null) {
          setProfile(pickDisplayProfile(map));
          setAccessStatus('choose_portal');
          return;
        }

        const targetKind = pathKind ?? choice ?? resolveDefaultPortalKind(map, pathname);
        if (!targetKind) {
          setProfile(pickDisplayProfile(map));
          setAccessStatus('choose_portal');
          return;
        }

        const targetAccess = portalSideAccess(map, targetKind);
        if (!targetAccess.hasAccess) {
          clearPortalChoice();
          setProfile(pickDisplayProfile(map));
          setAccessStatus('choose_portal');
          return;
        }

        if (targetKind === 'dem') {
          setActivePortalKind('dem');
          // IT/Management department (or elevated dem side): never fall through to
          // ensureDemUser — dem_allowed_emails role=user would overwrite IT.
          if (map.elevatedDepartment && targetAccess.profile) {
            setProfile(targetAccess.profile);
            setAccessStatus('allowed');
            setAuthError(null);
            setDeniedEmail(null);
            if (!showChooser) writePortalChoice('dem');
            return;
          }
          const demRole = targetAccess.profile?.role;
          if ((demRole === 'it' || demRole === 'management') && targetAccess.profile) {
            setProfile(targetAccess.profile);
            setAccessStatus('allowed');
            setAuthError(null);
            setDeniedEmail(null);
            if (!showChooser) writePortalChoice('dem');
            return;
          }
          const result = await withTimeout(
            ensureDemUser(user),
            ACCESS_VERIFY_TIMEOUT_MS,
            'ACCESS_VERIFY_TIMEOUT',
          );
          if (generation !== verifyGeneration.current) return;

          if (result.status === 'allowed') {
            // If department elevation resolved mid-flight, keep IT — never dem allowlist user.
            if (map.elevatedDepartment) {
              setProfile(targetAccess.profile ?? result.profile);
            } else {
              setProfile(result.profile);
            }
            setAccessStatus('allowed');
            setAuthError(null);
            setDeniedEmail(null);
            if (!showChooser) {
              writePortalChoice('dem');
            }
          } else if (result.status === 'not_on_allowlist') {
            // Elevated dept users are allowed even without dem allowlist row.
            if (map.elevatedDepartment && targetAccess.profile) {
              setProfile(targetAccess.profile);
              setAccessStatus('allowed');
              setAuthError(null);
              setDeniedEmail(null);
              if (!showChooser) writePortalChoice('dem');
              return;
            }
            setProfile(null);
            setAccessStatus('not_on_allowlist');
            setDeniedEmail(result.email);
          } else {
            setProfile(null);
            setAccessStatus('no_email');
            setDeniedEmail(null);
          }
          return;
        }

        if (targetKind === 'forms') {
          setActivePortalKind('forms');
          if (map.elevatedDepartment && targetAccess.profile) {
            setProfile(targetAccess.profile);
            setAccessStatus('allowed');
            setAuthError(null);
            setDeniedEmail(null);
            if (!showChooser) writePortalChoice('forms');
            return;
          }
          const result = await withTimeout(
            ensureFormsUser(user),
            ACCESS_VERIFY_TIMEOUT_MS,
            'ACCESS_VERIFY_TIMEOUT',
          );
          if (generation !== verifyGeneration.current) return;

          if (result.status === 'allowed') {
            setProfile(result.profile);
            setAccessStatus('allowed');
            setAuthError(null);
            setDeniedEmail(null);
            if (!showChooser) {
              writePortalChoice('forms');
            }
          } else if (result.status === 'not_on_allowlist') {
            setProfile(null);
            setAccessStatus('not_on_allowlist');
            setDeniedEmail(result.email);
          } else {
            setProfile(null);
            setAccessStatus('no_email');
            setDeniedEmail(null);
          }
          return;
        }

        if (targetKind === 'report' || targetKind === 'inventory') {
          setActivePortalKind('crm');
          setProfile(targetAccess.profile ?? pickDisplayProfile(map));
          setAccessStatus('allowed');
          setAuthError(null);
          setDeniedEmail(null);
          if (!showChooser) {
            writePortalChoice('report');
          }
          return;
        }

        if (targetKind === 'accounts') {
          setActivePortalKind('crm');
          setProfile(targetAccess.profile ?? pickDisplayProfile(map));
          setAccessStatus('allowed');
          setAuthError(null);
          setDeniedEmail(null);
          if (!showChooser) {
            writePortalChoice('accounts');
          }
          return;
        }

        if (targetKind === 'purchasing' || targetKind === 'design') {
          setActivePortalKind('crm');
          setProfile(targetAccess.profile ?? pickDisplayProfile(map));
          setAccessStatus('allowed');
          setAuthError(null);
          setDeniedEmail(null);
          if (!showChooser) {
            writePortalChoice(targetKind);
          }
          return;
        }

        if (IS_UNIFIED_PORTAL && !pathKind && choice && choice !== targetKind) {
          setProfile(pickDisplayProfile(map));
          setAccessStatus('choose_portal');
          return;
        }

        if (!IS_UNIFIED_PORTAL && showChooser && choice && pathKind && choice !== pathKind) {
          setProfile(pickDisplayProfile(map));
          setAccessStatus('choose_portal');
          return;
        }

        if (!showChooser) {
          writePortalChoice(targetKind);
        }

        setActivePortalKind(targetKind === 'it' ? 'it' : 'crm');

        if (map.elevatedDepartment && targetAccess.profile) {
          setProfile(targetAccess.profile);
          setAccessStatus('allowed');
          setAuthError(null);
          setDeniedEmail(null);
          return;
        }

        const result = await withTimeout(
          ensureCrmUser(user),
          ACCESS_VERIFY_TIMEOUT_MS,
          'ACCESS_VERIFY_TIMEOUT',
        );
        if (generation !== verifyGeneration.current) return;

        if (result.status === 'allowed') {
          setProfile(result.profile);
          setAccessStatus('allowed');
          setAuthError(null);
          setDeniedEmail(null);
        } else if (result.status === 'not_on_allowlist') {
          setProfile(null);
          setAccessStatus('not_on_allowlist');
          setDeniedEmail(result.email);
        } else {
          setProfile(null);
          setAccessStatus('no_email');
          setDeniedEmail(null);
        }
      } catch (e) {
        if (generation !== verifyGeneration.current) return;
        setProfile(null);
        setPortalAccess(null);
        setAccessStatus('idle');
        setAuthError(formatAuthError(e));
        console.error('[CRM] access verification failed', e);
      } finally {
        window.clearTimeout(failsafeTimer);
      }
    })();
  }, [session?.user?.id, session?.user?.email, accessRefreshKey]);

  const selfAllowlistEmail =
    session?.user != null
      ? normalizeEmail(resolveAuthEmail(session.user) ?? session.user.email ?? '') || null
      : null;

  usePortalAllowlistRealtime({
    enabled: Boolean(selfAllowlistEmail),
    emailFilter: selfAllowlistEmail,
    onChange: refreshAccessSilent,
  });

  const signInWithMicrosoft = useCallback(async () => {
    setAuthError(null);
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'azure',
      options: {
        redirectTo: getAuthCallbackRedirectUrl(),
        scopes: 'email openid profile',
        queryParams: {
          prompt: 'select_account',
        },
      },
    });

    if (error) throw error;
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      authUser: session?.user ?? null,
      profile,
      role: profile?.role ?? 'user',
      accessStatus,
      loading,
      configured: isSupabaseConfigured,
      authError,
      deniedEmail,
      portalAccess,
      clearAuthError,
      signInWithMicrosoft,
      signOut,
      refreshProfile,
      enterPortal,
      armPortalEntry,
    }),
    [
      session,
      profile,
      accessStatus,
      loading,
      authError,
      deniedEmail,
      portalAccess,
      clearAuthError,
      signInWithMicrosoft,
      signOut,
      refreshProfile,
      enterPortal,
      armPortalEntry,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
}
